import styled, { css } from 'styled-components';

import { NotificationBadge, MiniBadge } from '@xing-com/badge';
import { ProfileImage } from '@xing-com/profile-image';
import {
  dropShadowS,
  motionEasingEnter,
  motionTimeS,
  motionTimeXXS,
  scale005,
  scale010,
  scale070,
  scale080,
  scale090,
  scale100,
  scale110,
  scale130,
  spaceS,
  spaceXS,
  spaceXXS,
  xdlColorA11yBorder,
  xdlColorBackground,
  xdlColorButtonSecondaryHover,
  xdlColorControlBorder,
  xdlColorDisabledSoft,
  xdlColorFocusRing,
  xdlColorHoverQuaternary,
  xdlColorSelected,
  xdlColorText,
  xdlColorTextInvert,
  xdlColorTextSecondary,
  xdlColorTextTertiary,
} from '@xing-com/tokens';

import type { TagFontWeight } from './tag.types';

type BaseTagTProps = {
  $fontWeight?: TagFontWeight;
  $hideBorder?: boolean;
  $size?: keyof typeof sizes;
};

type DefaultTagTProps = {
  $fontWeight?: TagFontWeight;
  $hideBorder?: boolean;
  $profile?: boolean;
  $size?: keyof typeof sizes;
};

type PlaceholderTagTProps = {
  $fontWeight?: TagFontWeight;
  $hideBorder?: boolean;
  $profile?: boolean;
  $size?: keyof typeof sizes;
};

type InteractiveTagTProps = {
  $disabled?: boolean;
  $dragging?: boolean;
  $fontWeight?: TagFontWeight;
  $hideBorder?: boolean;
  $profile?: boolean;
  $selected?: boolean;
  $size?: keyof typeof sizes;
};

type DeleteButtonContainerTProps = {
  $size?: keyof typeof sizes;
};

type DeleteButtonTProps = {
  $size?: keyof typeof sizes;
};

type ProfileTProps = {
  $disabled?: boolean;
};

type ProfileImageContainerTProps = {
  $size?: keyof typeof sizes;
};

type BadgeContainerTProps = {
  $badgeSpacing?: boolean;
};

type MiniBadgeContainerTProps = {
  $disabled?: boolean;
};

type TextTruncationTProps = {
  $maxWidth?: string;
};

// *** Base Styles ***
const focusStyles = css`
  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }
`;

export const sizes = {
  small: css`
    height: ${scale110};
  `,
  medium: css`
    height: ${scale130};
  `,
};

const baseTag = css<BaseTagTProps>`
  align-items: center;
  border-radius: ${scale130};
  border: none;
  display: flex;
  font-size: ${scale070};
  line-height: ${scale080};
  padding-inline: ${spaceS};
  position: relative;
  z-index: 0;

  &::after {
    border-radius: ${scale130};
    border-style: solid;
    border-width: ${scale005};
    bottom: 0;
    content: '';
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: -1;
  }

  ${focusStyles};

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      default:
        return $size satisfies undefined;
    }
  }}

  ${(props) => css`
    ${props.$hideBorder &&
    css`
      &::after {
        border: none;

        @media (prefers-contrast: more) {
          border-style: solid;
          border-width: ${scale010};
          border-color: ${xdlColorA11yBorder};
        }
      }
    `};

    ${props.$fontWeight === 'bold' &&
    css`
      font-weight: bold;
    `};
  `};
`;

const defaultTag = css`
  color: ${xdlColorText};
  transition-duration: ${motionTimeS};
  transition-property: color;
  transition-timing-function: ${motionEasingEnter};

  &::after {
    background-color: ${xdlColorBackground};
    border-color: ${xdlColorControlBorder};
    transition-duration: ${motionTimeS}, ${motionTimeS}, ${motionTimeXXS};
    transition-property: background-color, border-color, transform;
    transition-timing-function: ${motionEasingEnter};
  }

  @media (prefers-contrast: more) {
    &::after {
      border-width: ${scale010};
      border-color: ${xdlColorA11yBorder};
    }
  }
`;

// *** Components ***
export const DefaultTag = styled.div<DefaultTagTProps>`
  ${baseTag};
  ${defaultTag};
`;

export const InteractiveTag = styled.div.attrs({
  tabIndex: 0,
})<InteractiveTagTProps>`
  ${baseTag};
  ${defaultTag};
  cursor: pointer;

  &:active {
    &&::after {
      border-color: transparent;
      height: calc(100% - ${spaceXXS} * 2);
      inset-block-start: ${spaceXXS};
      inset-inline-start: ${spaceXXS};
      width: calc(100% - ${spaceXXS} * 2);
    }
  }

  &:hover {
    background-color: transparent;
    color: ${xdlColorText};

    &::after {
      background-color: ${xdlColorButtonSecondaryHover};
      border-color: transparent;
      height: calc(100% + ${spaceXXS} * 2);
      inline-size: calc(100% + ${spaceXXS} * 2);
      inset-block-start: -${spaceXXS};
      inset-inline-start: -${spaceXXS};
    }

    @media (prefers-contrast: more) {
      &::after {
        border-width: ${scale010};
        border-color: ${xdlColorA11yBorder};
      }
    }
  }

  ${(props) => css`
    ${props.$selected &&
    css`
      color: ${xdlColorTextInvert};

      &::after {
        background-color: ${xdlColorSelected};
        border-color: ${xdlColorSelected};
      }

      &:hover {
        color: ${xdlColorTextInvert};

        &::after {
          background-color: ${xdlColorHoverQuaternary};
          border-color: ${xdlColorHoverQuaternary};
        }
      }
    `};

    ${props.$dragging &&
    css`
      &::after {
        box-shadow: ${dropShadowS};
      }
    `};

    ${props.$profile &&
    css`
      &:active {
        color: ${xdlColorBackground};

        &::after {
          background-color: ${xdlColorSelected};
        }
      }
    `};

    ${props.$disabled &&
    css`
      &&,
      &&:focus,
      &&:hover {
        color: ${xdlColorTextTertiary};
        cursor: default;
        overflow: hidden;
        user-select: none;
        pointer-events: none;

        &::after {
          background-color: ${xdlColorDisabledSoft};
          border-color: ${xdlColorDisabledSoft};
        }
      }

      &&&:active::after {
        transform: none;
      }
    `};
  `};
`;

export const PlaceholderTag = styled.div<PlaceholderTagTProps>`
  ${baseTag};

  &,
  &:focus,
  &:hover {
    color: ${xdlColorTextSecondary};

    &::after {
      background-color: ${xdlColorBackground};
      border-color: ${xdlColorControlBorder};
      border-style: dashed;
      border-width: ${scale005};
    }
  }
`;

export const IconContainer = styled.div`
  align-content: center;
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: ${scale090};
  justify-content: center;
  width: ${scale090};

  & > svg {
    height: 100%;
    width: 100%;
  }
`;

export const ProfileImageContainer = styled.div<ProfileImageContainerTProps>`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: ${scale110};
  justify-content: center;
  width: ${scale110};

  ${(props) => css`
    ${props.$size === 'small' &&
    css`
      height: ${scale090};
      transform: scale(0.75);
      width: ${scale090};
    `};
  `};
`;

export const BadgeContainer = styled.div<BadgeContainerTProps>`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  justify-content: center;

  ${(props) => css`
    ${props.$badgeSpacing &&
    css`
      margin-inline-start: ${spaceXS};
    `};
  `};
`;

export const MiniBadgeContainer = styled(MiniBadge)<MiniBadgeContainerTProps>`
  margin-inline: ${spaceXS};

  ${(props) => css`
    ${props.$disabled &&
    css`
      opacity: 0.4;
    `};
  `};
`;

export const SlotContainer = styled.div`
  max-height: ${scale100};
`;

export const TextTruncationWrapper = styled.div`
  align-items: center;
  display: flex;
  height: ${scale130};
  overflow: hidden;
`;

export const TextTruncation = styled.span<TextTruncationTProps>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-inline: ${spaceXS};

  ${(props) => css`
    ${props.$maxWidth &&
    css`
      max-width: ${props.$maxWidth};
    `};
  `};
`;

export const Badge = styled(NotificationBadge)`
  color: ${xdlColorText};
  background-color: ${xdlColorTextInvert};
  border-color: ${xdlColorTextInvert};
`;

export const Profile = styled(ProfileImage)<ProfileTProps>`
  height: ${scale110};
  width: ${scale110};

  ${(props) => css`
    ${props.$disabled &&
    css`
      opacity: 0.4;
    `};
  `};
`;

export const DeleteButtonContainer = styled(
  InteractiveTag
)<DeleteButtonContainerTProps>`
  height: ${scale130};
  line-height: ${scale080};
  padding-inline: 0;

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      default:
        return $size satisfies undefined;
    }
  }}
`;

// The right clickable area for selecting the tag
export const DeleteButton = styled.button<DeleteButtonTProps>`
  align-items: center;
  background: transparent;
  border-radius: ${scale130} 0 0 ${scale130};
  border: none;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: ${scale130};
  line-height: ${scale080};
  padding-inline-end: 0;
  padding-inline-start: ${spaceS};
  position: relative;
  z-index: 1;

  ${focusStyles};

  &[disabled] {
    cursor: default;
  }

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      default:
        return $size satisfies undefined;
    }
  }}
`;

export const DeleteButtonAction = styled.button`
  align-items: center;
  background-color: transparent;
  border-radius: 0 ${scale130} ${scale130} 0;
  border: none;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: ${scale130};
  line-height: ${scale080};
  padding-inline-start: calc(${spaceXS} * -1);
  position: relative;
  z-index: 1;

  ${focusStyles};

  &[disabled] {
    cursor: default;
  }
`;
